import { createFileRoute } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Camera } from 'lucide-react';
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { useState } from "react";
import { trackWhatsAppClick } from "@/lib/gtm";

export const Route = createFileRoute("/contato")({
  component: ContactPage,
});

function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    whatsapp: "",
    email: "",
    location: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.whatsapp || !formData.email || !formData.location || !formData.message) {
      alert("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    const message = `Olá, tudo bem? Estava no site e preciso de atendimento.

Meus dados:
Nome: ${formData.name}
WhatsApp: ${formData.whatsapp}
E-mail: ${formData.email}
Cidade/Bairro: ${formData.location}

Mensagem:
${formData.message}`;

    const whatsappUrl = `https://wa.me/5521992698354?text=${encodeURIComponent(message)}`;
    trackWhatsAppClick("Enviar Mensagem", whatsappUrl, "contato-form");
    window.open(whatsappUrl, "_blank");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <SEO 
        title="Contato | STOP SOM Janelas Acústicas no Rio de Janeiro"
        description="Fale com a STOP SOM Janelas Acústicas. Atendimento em todo o Rio de Janeiro e fábrica em Duque de Caxias - RJ."
      />
      <div className="bg-white">
      {/* Hero Interno */}
      <section className="pt-40 pb-20 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl text-center lg:text-left mx-auto lg:mx-0"
          >
            <h1 className="text-3xl md:text-5xl lg:text-7xl font-bold mb-6">Fale com a STOP SOM Janelas Acústicas</h1>
            <p className="text-2xl opacity-90 leading-relaxed">
              Atendemos clientes em todo o Rio de Janeiro com soluções sob medida em janelas acústicas, esquadrias acústicas e projetos antirruído. Nossa fábrica está localizada em Duque de Caxias - RJ.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 px-6 pb-40 md:pb-24">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Informações de Contato */}
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-12">Informações de Contato</h2>
              <div className="grid sm:grid-cols-2 gap-8 mb-12">
                <div className="flex gap-4">
                  <div className="bg-secondary/10 p-3 rounded-xl text-secondary shrink-0 h-fit">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Telefone</h3>
                    <a href="tel:+5521992698354" className="text-gray-500 hover:text-secondary transition-colors">(21) 99269-8354</a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-secondary/10 p-3 rounded-xl text-secondary shrink-0 h-fit">
                    <WhatsAppIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">WhatsApp</h3>
                    <a 
                      href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      onClick={() => trackWhatsAppClick("(21) 99269-8354", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "contato")}
                      data-gtm-event="whatsapp_click"
                      data-conversion="whatsapp"
                      data-button-location="contato"
                      className="text-gray-500 hover:text-secondary transition-colors"
                    >(21) 99269-8354</a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-secondary/10 p-3 rounded-xl text-secondary shrink-0 h-fit">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">E-mail</h3>
                    <p className="text-gray-500">contato@stopsom.com.br</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-secondary/10 p-3 rounded-xl text-secondary shrink-0 h-fit">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Área de Atendimento</h3>
                    <p className="text-gray-500">Todo o Rio de Janeiro - RJ</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-secondary/10 p-3 rounded-xl text-secondary shrink-0 h-fit">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Horário</h3>
                    <p className="text-gray-500">Seg a Sex, 09h às 18h</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-[3rem] border border-gray-100 mb-12">
                <div className="flex gap-4 mb-6">
                  <div className="bg-primary p-3 rounded-xl text-white shrink-0 h-fit">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Fábrica</h3>
                    <p className="text-gray-500">Rua Itatinga, 511 — Duque de Caxias - RJ</p>
                  </div>
                </div>
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento." 
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("Chamar no WhatsApp", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "contato")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="contato"
                  className="w-full bg-primary text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-secondary transition-all shadow-xl shadow-primary/20"
                >
                  <WhatsAppIcon className="w-6 h-6" /> Chamar no WhatsApp
                </a>
              </div>

              {/* Redes Sociais */}
              <div className="flex items-center gap-6">
                <h4 className="font-bold text-primary uppercase tracking-wider text-sm">Siga-nos:</h4>
                <a href="https://www.instagram.com/stopsomrj" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-primary hover:bg-secondary hover:text-white transition-all"><Camera size={20} /></a>
              </div>
            </div>

            {/* Formulário */}
            <div className="bg-white p-10 lg:p-16 rounded-[3rem] shadow-2xl border border-gray-50 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/5 rounded-full translate-x-1/2 -translate-y-1/2" />
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary mb-8 relative z-10">Envie uma mensagem</h2>
              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">Nome Completo</label>
                    <input 
                      type="text" 
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" 
                      placeholder="Ex: João Silva" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-gray-700 ml-2">WhatsApp</label>
                    <input 
                      type="tel" 
                      name="whatsapp"
                      required
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" 
                      placeholder="(21) 99269-8354" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-2">E-mail</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" 
                    placeholder="joao@email.com" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-2">Cidade/Bairro</label>
                  <input 
                    type="text" 
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium" 
                    placeholder="Ex: Copacabana, RJ" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700 ml-2">Mensagem</label>
                  <textarea 
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 rounded-2xl bg-gray-50 border-transparent focus:border-secondary focus:bg-white focus:ring-0 transition-all font-medium min-h-[150px]" 
                    placeholder="Como podemos ajudar?" 
                  />
                </div>
                <button type="submit" className="w-full bg-primary text-white py-5 rounded-2xl font-bold text-lg hover:bg-secondary transition-all shadow-xl shadow-primary/20">
                  Enviar Mensagem
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* JSON-LD LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "STOP SOM Janelas Acústicas",
          "image": "https://stopsomesquadrias.com.br/logo.png",
          "@id": "https://stopsomesquadrias.com.br",
          "url": "https://stopsomesquadrias.com.br",
          "telephone": "+55 21 99269-8354",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua Itatinga, 511",
            "addressLocality": "Duque de Caxias",
            "addressRegion": "RJ",
            "postalCode": "25000-000",
            "addressCountry": "BR"
          },
          "areaServed": {
            "@type": "State",
            "name": "Rio de Janeiro"
          },
          "description": "Empresa especializada em janelas acústicas, esquadrias acústicas e soluções antirruído sob medida no Rio de Janeiro, com fábrica em Duque de Caxias - RJ."
        })}
      </script>
    </div>
    </>
  );
}